import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip } from "../../ui/record";
import { area, field, refuse } from "../clients/forms";
import { pageHead } from "../empty";

// Settings › Business profiles (brief 1E-3, mockup v55 settings/business): what prints on the
// letterhead, invoices and forms, one tab per business. Brief 1E-14 (mockup v64): one NPI and the
// federal tax ID (EIN), the business's own; claims print them when it is the billing or rendering provider.
// Admin; every save is in the business's history.
// Brief 1E-12 (mockup v62): Add business (the sheet; the new business's tab opens), the Used by
// card (the modules that run under it, Pays for FlowCare, Change in Modules) and Archive with a
// reason when nothing uses it, else the button is off with the reasons as its tip. Archived
// businesses leave the tabs.

interface Business {
  id: string;
  legal_name: string;
  name_line: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  fax: string | null;
  npi: string | null;
  from_email: string | null;
  ein: string;
  usedBy: string[];
  paysForFlowCare: boolean;
  canArchive: boolean;
  archiveReasons: string[];
}

type Key = Exclude<
  keyof Business,
  "id" | "usedBy" | "paysForFlowCare" | "canArchive" | "archiveReasons"
>;

export async function businessesScreen(host: HTMLElement, ctx: ScreenContext) {
  const res = await api<{ businesses: Business[] }>("GET", "/api/settings/businesses");
  const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
  const addIcon = el(
    "button",
    { type: "button", class: "btn ib", "aria-label": "Add business", "data-tip": "Add business" },
    icon("plus"),
  );
  const head = pageHead(
    ctx,
    "What prints on letterhead, invoices and forms. Change it here and every new printout follows.",
    res.ok && res.body.businesses.length ? [addIcon, save] : [],
  );
  host.append(head);
  if (!res.ok) {
    host.append(
      el(
        "p",
        { class: "note", role: "alert" },
        "Only an Admin can see and change business profiles.",
      ),
    );
    return;
  }
  const all = res.body.businesses;
  addIcon.addEventListener("click", () => addBusiness(added));
  const tabs = el("div", { class: "tabs", role: "tablist" });
  const body = el("div", {});
  let cur: Business | undefined = all.find((b) => b.usedBy.length) ?? all[0];
  let inputs = new Map<Key, HTMLInputElement>();

  function added(b: Business) {
    all.push(b);
    cur = b;
    if (all.length === 1) {
      host.replaceChildren();
      void businessesScreen(host, ctx);
      return;
    }
    draw();
  }

  if (!cur) {
    const first = el("button", { type: "button", class: "btn pri" }, icon("plus"), " Add business");
    first.addEventListener("click", () => addBusiness(added));
    head.querySelector(".actions")?.append(first);
    host.append(
      el(
        "section",
        { class: "card empty" },
        el("h2", {}, "No business yet"),
        el(
          "p",
          { class: "small muted" },
          "Add each legal business the company runs, with its own name and EIN.",
        ),
      ),
    );
    return;
  }
  host.append(tabs, body);

  const fieldOf = (b: Business, key: Key, label: string, hint?: string) => {
    const id = `bz-${key}`;
    const input = el("input", { type: "text", id, value: (b[key] as string | null) ?? "" });
    inputs.set(key, input);
    return el(
      "div",
      { class: "field" },
      el(
        "label",
        { for: id },
        label,
        hint ? el("span", { class: "small muted" }, ` ${hint}`) : null,
      ),
      input,
    );
  };

  function usedByCard(b: Business) {
    const archive = el(
      "button",
      { type: "button", class: "btn ib sm", "aria-label": `Archive ${b.legal_name}` },
      icon("archive"),
    );
    let archiveSlot: HTMLElement = archive;
    if (b.canArchive) {
      archive.setAttribute("data-tip", "Archive with a reason");
      archive.addEventListener("click", () => archiveBusiness(b));
    } else {
      archive.disabled = true;
      // A disabled button gets no hover or focus, so the tip sits on its wrapper.
      archive.style.pointerEvents = "none";
      archiveSlot = el(
        "span",
        { "data-tip": b.archiveReasons.join(" "), "data-archive-reasons": "", tabindex: "0" },
        archive,
      );
    }
    const change = el(
      "button",
      { type: "button", class: "btn sm ghost" },
      icon("sliders"),
      " Change in Modules",
    );
    change.addEventListener("click", () => ctx.go("/settings/modules"));
    return el(
      "section",
      { class: "card", "aria-label": "Used by" },
      el(
        "div",
        { class: "cardhead" },
        el(
          "div",
          {},
          el("h2", {}, icon("apps"), " Used by"),
          el(
            "span",
            { class: "small muted" },
            "Invoices, claims, the payroll file and card payments for these modules go out under this business.",
          ),
        ),
        archiveSlot,
      ),
      el(
        "div",
        { style: "display:flex;gap:8px;flex-wrap:wrap;align-items:center" },
        ...(b.usedBy.length
          ? b.usedBy.map((m) => chip(m, "info"))
          : [el("span", { class: "small muted" }, "No module yet.")]),
        b.paysForFlowCare ? chip("Pays for FlowCare", "ok") : null,
        change,
      ),
    );
  }

  function draw() {
    const b = cur as Business;
    tabs.replaceChildren(
      ...all.map((x) => {
        const t = el(
          "button",
          { type: "button", role: "tab", "aria-selected": String(x.id === b.id) },
          x.legal_name,
        );
        t.addEventListener("click", () => {
          cur = x;
          draw();
        });
        return t;
      }),
    );
    inputs = new Map();
    const two = (...f: HTMLElement[]) => el("div", { class: "grid2", style: "gap:10px" }, ...f);
    body.replaceChildren(
      usedByCard(b),
      el(
        "section",
        { class: "card" },
        el(
          "form",
          { class: "form", style: "max-width:none" },
          fieldOf(b, "legal_name", "Legal / display name"),
          fieldOf(b, "name_line", "Line under the name"),
          fieldOf(b, "address_line1", "Address"),
          fieldOf(b, "address_line2", "Address line 2"),
          el(
            "div",
            { class: "grid3", style: "gap:10px" },
            fieldOf(b, "city", "City"),
            fieldOf(b, "state", "State"),
            fieldOf(b, "zip", "ZIP"),
          ),
          two(fieldOf(b, "phone", "Phone"), fieldOf(b, "fax", "Fax")),
          two(fieldOf(b, "npi", "NPI"), fieldOf(b, "from_email", "Email")),
          el(
            "div",
            { class: "grid2", style: "gap:10px;margin-top:10px" },
            fieldOf(b, "ein", "Federal tax ID (EIN)"),
          ),
          el(
            "p",
            { class: "small muted" },
            "Claims print this business's NPI and EIN when it is the billing or rendering provider.",
          ),
        ),
      ),
    );
  }

  function archiveBusiness(b: Business) {
    const reason = area("Why is it archived?", "");
    const go = el("button", { type: "button", class: "btn pri" }, icon("archive"), " Archive");
    const m = openModal({
      title: `Archive ${b.legal_name}`,
      sub: "Past invoices and claims keep its name.",
      body: [el("div", { class: "form", style: "max-width:none" }, reason.el)],
      actions: [go],
    });
    reason.input.focus();
    go.addEventListener("click", async () => {
      if (!reason.input.value.trim()) return refuse(m.note, "Say why it's archived.");
      go.disabled = true;
      const r = await api("POST", `/api/settings/businesses/${b.id}/archive`, {
        reason: reason.input.value,
      });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      const i = all.findIndex((x) => x.id === b.id);
      if (i >= 0) all.splice(i, 1);
      toast(`${b.legal_name} archived`, "Past invoices and claims keep its name");
      cur = all.find((x) => x.usedBy.length) ?? all[0];
      if (!cur) {
        host.replaceChildren();
        void businessesScreen(host, ctx);
        return;
      }
      draw();
    });
  }

  save.addEventListener("click", async () => {
    const b = cur as Business;
    save.disabled = true;
    const values = Object.fromEntries([...inputs].map(([k, i]) => [k, i.value]));
    const r = await api<Business>("PUT", `/api/settings/businesses/${b.id}`, values);
    save.disabled = false;
    if (!r.ok) return toast("Not saved", r.body.error);
    const i = all.findIndex((x) => x.id === r.body.id);
    if (i >= 0) all[i] = r.body;
    cur = r.body;
    toast(`${r.body.legal_name} profile saved`, "Every new printout and claim follows");
    draw();
  });
  draw();
}

/** The Add business sheet (mockup v62); on save the new business's tab opens. */
function addBusiness(done: (b: Business) => void) {
  const name = field("Legal name", "", { placeholder: "e.g. Sample Home Health LLC" });
  const line = field("Line under the name", "", {
    placeholder: "e.g. Home Health RSA · Lic. #00000",
  });
  const ein = field("Federal tax ID (EIN)", "", {
    placeholder: "00-0000000",
    inputmode: "numeric",
  });
  const phone = field("Phone", "", { type: "tel" });
  const addr = field("Address", "");
  const city = field("City", "");
  const state = field("State", "", { maxlength: "2" });
  const zip = field("ZIP", "", { inputmode: "numeric" });
  const go = el("button", { type: "submit", class: "btn pri" }, icon("check"), " Add business");
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const form = el(
    "form",
    { class: "form", style: "max-width:none" },
    name.el,
    line.el,
    el("div", { class: "grid2", style: "gap:10px" }, ein.el, phone.el),
    addr.el,
    el("div", { class: "grid3", style: "gap:10px" }, city.el, state.el, zip.el),
    el(
      "p",
      { class: "small muted", style: "margin:0" },
      "It starts with one location at this address. NPI, logo and sending details are on its profile.",
    ),
    el("div", { class: "actions" }, go, cancel),
  );
  const m = openModal({
    title: "Add business",
    sub: "A legal business the company runs. Each one has its own EIN.",
    body: [form],
  });
  cancel.addEventListener("click", () => m.close());
  name.input.focus();
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    go.disabled = true;
    const r = await api<Business>("POST", "/api/settings/businesses", {
      legal_name: name.input.value,
      name_line: line.input.value,
      ein: ein.input.value,
      phone: phone.input.value,
      address_line1: addr.input.value,
      city: city.input.value,
      state: state.input.value,
      zip: zip.input.value,
    });
    go.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(`${r.body.legal_name} added`, "Pick which modules use it in Modules");
    done(r.body);
  });
}
